import React from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { getProp, setProp } from '../Utils/formHelpers';
import CalendarIcon from '../Icons/CalendarIcon';
import Validation from '../Validation';
import doesNodeContainClick from '../Utils/doesNodeContainClick';
import { FormWrapperPropTypes } from '../Form/withForm';
import { parseDate, convertDateToISO, formatDate } from '../Utils/dateHelper';
import { getTitle, getRandomId, Presentation } from '../Utils/baseComponentHelper';
import useActivation from '../Hooks/useActivation';
import useValidations from '../Hooks/useValidations';
import ValidationViewer from '../Validation/ValidationViewer';
import DateComponentChooser from './DateComponentChooser';
/**
Componente per l'inserimento di una data.
La data può essere inserita digitando un testo o selezionando di un valore dalla pick list.

Per l'inserimento della data (prendiamo ad esempio 27/09/2018), oltre al formato dd/mm/yyyy, sono ammessi i seguienti formati:

- ddmmyyyy (27092018)
- ddmmyy (270918)
 */
class DatePicker extends React.Component {
  constructor(props) {
    super(props);
    const date = props.date || new Date();
    this.state = {
      show: props.show || false,
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      date: props.date,
      dirty: false,
    };
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.parseDate = parseDate;
    this.uniqueid = getRandomId();
  }

  componentDidMount() {
    const { name, addToForm } = this.props;
    this.updateProps(this.props);
    document.addEventListener('click', this.closeIfClickOutside);
    if (addToForm && name) {
      addToForm(name, this.valida(), this.reset);
    }
  }

  UNSAFE_componentWillReceiveProps(props) {
    this.updateProps(props);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.closeIfClickOutside);
    const { name, removeFromForm } = this.props;

    if (removeFromForm && name) {
      removeFromForm(name);
    }
  }

  updateProps = ({
    date, show, name, stato, readOnly, disabled,
  }) => {
    const { show: showS, date: dateS } = this.state;
    if (show !== showS) {
      this.setState({ show });
    }

    let newDate = null;
    if (stato && name) {
      newDate = getProp(name, stato);

      if (newDate === '') {
        this.updateValue('', { readOnly, disabled });
      }

      if (newDate && newDate !== new Date(dateS)) {
        newDate = new Date(newDate);
        this.setDate(newDate);
        this.updateValue(formatDate(newDate.getDate(), newDate.getMonth() + 1, newDate.getFullYear(), '/'), { readOnly, disabled });
      } else {
        newDate = new Date();
        this.setState({
          day: newDate.getDate(),
          month: newDate.getMonth(),
          year: newDate.getFullYear(),
        });
      }
    } else if (!date) {
      this.setDate(Date.now());
      this.updateValue('', { readOnly, disabled });
    } else {
      this.setDate(date);
      this.updateValue(formatDate(date.getDate(), date.getMonth() + 1, date.getFullYear(), '/'), { readOnly, disabled });
    }
  };

  getTitle = () => {
    const { title, validations } = this.props;
    return getTitle(title, validations);
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  isValid = () => this.valida().valid;

  reset = () => {
    this.setState({ dirty: false });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  clear = () => {
    const { readOnly } = this.props;
    this.refDatepickerInput.value = new Presentation({ readOnly }).format('');
  };

  setDate = (d, hide) => {
    const newD = d != null ? new Date(d) : new Date();
    const newState = {
      year: newD.getFullYear(),
      day: newD.getDate(),
      month: newD.getMonth(),
    };
    if (hide) {
      newState.show = false;
    } else {
      newState.date = newD;
    }
    this.setState(newState);
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  selectDate = (newDay) => {
    const { year, month } = this.state;
    const newDate = new Date(year, month, newDay);
    this.setState({
      day: newDay, date: newDate, show: false, dirty: true,
    });
    this.notifica(`${newDay}/${month}/${year}`);
  };

  selectFullDate = ({ year, month, day }) => {
    const newDate = new Date(year, month, day);
    this.setState({
      day, date: newDate, show: false, dirty: true,
    });
    this.notifica(`${day}/${month}/${year}`);
  };

  valida = () => {
    const {
      validations, name, date, stato,
    } = this.props;
    if (!validations) return { valid: true };
    return Validation.validateAll(validations, (stato && name && getProp(name, stato)) || date || '');
  };

  onBlur = (evt) => {
    const { futureDate, readOnly, disabled } = this.props;
    if (readOnly || disabled) return;
    const { dirty } = this.state;
    if (!dirty) {
      this.setState({ dirty: true });
    }
    const date = parseDate(new Presentation({ readOnly }).parse(evt.target.value), futureDate, true);
    if (date != null) {
      if (this.isOutOfRange(date)) {
        this.updateValue('');
        this.notifica('');
        this.setState({ date: null });
      } else {
        this.notifica(`${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`);
      }
    } else {
      this.updateValue('');
      this.notifica('');
      const d = new Date();
      this.setState({
        date: null, day: d.getDate(), month: d.getMonth(), year: d.getFullYear(),
      });
    }
  };

  notifica = (date) => {
    const {
      futureDate, onChange, name, onFormChange, stato,
    } = this.props;
    const newDate = parseDate(date, futureDate, false);
    if (newDate == null) {
      this.updateValue('');
    } else {
      this.updateValue(formatDate(newDate.getDate(), newDate.getMonth() + 1, newDate.getFullYear(), '/'));
      this.setDate(newDate, false);
    }
    onChange && onChange(newDate);
    if (onFormChange && name) {
      const obj = {};
      setProp(name, newDate ? convertDateToISO(newDate) : null, obj, stato);
      onFormChange(obj, name, newDate ? convertDateToISO(newDate) : null);
    }
  };

  closeIfClickOutside = (e) => {
    const { show } = this.state;
    if (document.contains(e.target) && !doesNodeContainClick(this.refNode, e) && show) {
      this.setState({
        show: false,
      });
      this.updateStatus();
    }
  };

  changeVisibility = () => {
    const { disabled } = this.props;
    if (disabled) return;
    const { show } = this.state;
    if (show) {
      this.updateStatus();
    } else {
      this.setState({ show: true });
    }
  };

  updateStatus = () => {
    const { date } = this.state;
    const newDate = date || new Date();
    this.setDate(newDate, true);
  };

  getCurrentSelectedDate = () => {
    let dataCorrentementeSelezionata = null;
    const { stato, name } = this.props;
    if (stato && name) {
      const formData = getProp(name, stato);
      if (formData != null && formData !== '') {
        const d = new Date(formData);
        dataCorrentementeSelezionata = formatDate(d.getDate(), d.getMonth() + 1, d.getFullYear(), '/');
      }
    } else {
      const { date } = this.state;
      if (date && !isNaN(date)) {
        dataCorrentementeSelezionata = formatDate(date.getDate(), date.getMonth() + 1, date.getFullYear(), '/');
      }
    }
    return dataCorrentementeSelezionata;
  };

  updateValue = (value, props = undefined) => {
    const { disabled: cDisabled, readOnly: cReadOnly } = this.props;
    const { readOnly } = useActivation(
      props ? props.disabled : cDisabled,
      props ? props.readOnly : cReadOnly,
    );
    if (this.refDatepickerInput) {
      this.refDatepickerInput.value = new Presentation({ readOnly }).format(value);
    }
  };

  isOutOfRange = (date) => {
    const { range } = this.props;
    if (!range) return false;
    const mills = date.getTime();
    if (range.from && mills < range.from.getTime()) {
      return true;
    }
    if (range.to && mills > range.to.getTime()) {
      return true;
    }
    return false;
  };

  render() {
    const {
      isValid, name, validare, right,
      readOnly: readOnlyProp, disabled: disabledProp,
      className, autoFocus, title, actions, placeholder, range,
    } = this.props;
    const {
      dirty, show, date, year, month, day,
    } = this.state;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { disabled, readOnly } = useActivation(disabledProp, readOnlyProp);
    const validazione = this.valida();
    if (isValid != null && name) {
      isValid(name, validazione.valid, validazione.messages || []);
    }
    const data = this.getCurrentSelectedDate();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { hasError, hasWarning } = useValidations(validare, dirty, validazione);

    const wrapperClasses = cns(
      'datePicker__inputWrapper',
      {
        'input--invalid': hasError,
        'input--readOnly': readOnly,
        'input--valid': validazione.valid && (validare || dirty),
      },
    );
    const classes = cns(
      'datePicker',
      {
        'datePicker--right': right,
        'datePicker--disabled': disabled,
        'datePicker--open': show === true,
      },
      className,
    );
    const label = name || title;
    return (
      <div className={classes} ref={(r) => { this.refNode = r; }}>
        <div className="datePicker__container">
          <div className={wrapperClasses}>
            <input
              id={this.uniqueid}
              autoComplete={`datepicker-${this.uniqueid}`}
              autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
              className="datePicker__input validationViewer__target"
              defaultValue={new Presentation({ readOnly }).format(data)}
              disabled={disabled}
              readOnly={readOnly}
              data-origin={name}
              onBlur={this.onBlur}
              ref={(r) => { this.refDatepickerInput = r; }}
              type="text"
              placeholder={placeholder}
            />
            {label && <label htmlFor={this.uniqueid} className="input__label">{label}</label>}
            {title && <span className="datePicker__title">{this.getTitle()}</span>}
            <span className="datePicker__bar" />
            {!readOnly && <CalendarIcon onClick={this.changeVisibility} />}
            <ValidationViewer hasError={hasError} hasWarning={hasWarning} validazione={validazione} />
          </div>
          {show && (
            <DateComponentChooser
              date={date && { year, month, day }}
              view={DateComponentChooser.Views.DAY}
              onChange={(newDate) => {
                this.selectFullDate(newDate);
              }}
              component={DateComponentChooser.Views.DAY}
              range={range}
            />
          )}
        </div>
        {!readOnly && actions && <div className="datePicker__actions">{actions}</div>}
      </div>
    );
  }
}

DatePicker.defaultProps = {
  actions: null,
  autoFocus: false,
  className: null,
  date: null,
  disabled: false,
  readOnly: false,
  futureDate: false,
  name: null,
  onChange: null,
  range: null,
  right: false,
  show: false,
  title: null,
  validations: null,
  placeholder: undefined,
};

DatePicker.propTypes = {
  ...FormWrapperPropTypes,
  /** Icone da mostrare a lato del datepicker */
  actions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /** Indica se fare focus di default */
  autoFocus: PropTypes.bool,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Data visualizzata di default */
  date: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Presenta il campo in modalità sola lettura */
  readOnly: PropTypes.bool,
  /** Indica se voglio inserire date nel futuro
     * e quindi preinserire l'anno 2000 se indico la data con due cifre */
  futureDate: PropTypes.bool,
  /** Nome del campo da bindare se il componente è contenuto all'interno di un componente Form */
  name: PropTypes.string,
  /**
   * Callback invocata alla conferma di una data:
   * ```
   *  onChange(date)
   * ```
   * dove:
   * * *date*: data confermata
   */
  onChange: PropTypes.func,
  /**
   * Indica il range di date attive, è possibile passare anche solo una della due proprietà
   * per creare valori iniziali minimi o valori finali massimi
   */
  range: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    from: PropTypes.any,
    // eslint-disable-next-line react/forbid-prop-types
    to: PropTypes.any,
  }),
  /** Indica se aprire il menu nell'altro senso */
  right: PropTypes.bool,
  /** Indica se visualizzare o meno la tendina di selezione */
  show: PropTypes.bool,
  /** Etichetta da visualizzare nel campo che renderizza il componente */
  title: PropTypes.string,
  /** Lista di validazioni da applicare al campo */
  validations: PropTypes.arrayOf(PropTypes.oneOfType(
    [
      PropTypes.string,
      PropTypes.object,
      PropTypes.func,
    ],
  )),

  /** Esempio testuale del dato da inserire nel campo */
  placeholder: PropTypes.string,
};

export default DatePicker;
